import React, { useState, useRef } from "react";
import axios from "axios";
import Bugsnag from "@bugsnag/js";
import constants from "../../Constants";
import "./QuoteImageUpload.css";

const baseURL = constants.baseURL;
const MAX_IMAGES = 20;
const MAX_FILE_SIZE_MB = 10;

/**
 * QuoteImageUpload Component
 * Allows customers to upload images of items they want to move
 * Images are stored in S3 and can be viewed by admins, drivers, and AI
 */
const QuoteImageUpload = ({ 
  quoteId, 
  quoteSessionId,
  images = [], 
  onImagesChange,
  prospectEmail,
  prospectMobile 
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Priority: quoteId > quoteSessionId > fallback
  // quoteSessionId links images to quotes before the quote is created
  const getQuoteRef = () => {
    if (quoteId) return quoteId;
    if (quoteSessionId) return quoteSessionId;
    // Fallback - use email/mobile hash
    const ref = prospectEmail || prospectMobile || `temp-${Date.now()}`;
    return `prospect-${ref.replace(/[^a-zA-Z0-9]/g, "")}`;
  };

  const validateFile = (file) => {
    // Check file type
    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
    if (!validTypes.includes(file.type)) {
      return "Please upload JPG, PNG, or WebP images only";
    }
    
    // Check file size
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB`;
    }
    
    return null;
  };

  const handleFiles = async (files) => {
    const fileArray = Array.from(files);
    const remainingSlots = MAX_IMAGES - images.length;
    
    if (fileArray.length > remainingSlots) {
      setError(`You can only add ${remainingSlots} more image${remainingSlots === 1 ? "" : "s"}`);
      return;
    }

    // Upload files sequentially and accumulate results
    let updatedImages = [...images];
    let uploadIndex = 0;
    for (const file of fileArray) {
      // Pass current count to check limit correctly
      const uploadedImage = await uploadImageWithCount(file, updatedImages.length, uploadIndex);
      if (uploadedImage) {
        updatedImages = [...updatedImages, uploadedImage];
        onImagesChange(updatedImages);
      }
      uploadIndex++;
    }
  };

  // Version of uploadImage that takes current count to avoid stale closure issue
  const uploadImageWithCount = async (file, currentCount, index) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return null;
    }

    if (currentCount >= MAX_IMAGES) {
      setError(`Maximum ${MAX_IMAGES} images allowed`);
      return null;
    }

    setError("");
    setUploading(true);
    setUploadProgress(0);

    try {
      // Get presigned URL from API
      const response = await axios.post(baseURL + "/quote-image-upload-url", {
        file: {
          name: file.name,
          originalname: file.name,
          size: file.size,
          type: file.type,
          mimetype: file.type,
        },
        quoteRef: getQuoteRef(),
        quoteSessionId: quoteSessionId,
        prospectEmail,
        prospectMobile,
      });

      const presignedUrl = response.data.uploadUrl;
      const fileKey = response.data.fileKey;

      // Upload to S3
      await axios.put(presignedUrl, file, {
        headers: {
          "Content-Type": file.type,
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        },
      });

      setUploading(false);
      setUploadProgress(0);

      // Return the uploaded image info (use unique id with index to avoid duplicates)
      return {
        id: Date.now() + index,
        key: fileKey,
        name: file.name,
        url: `https://media.qiktruck.co.za/${fileKey}`,
        thumbnail: URL.createObjectURL(file),
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString(),
      };
    } catch (err) {
      console.error("Upload error:", err);
      Bugsnag.notify(err);
      setError("Failed to upload image. Please try again.");
      setUploading(false);
      setUploadProgress(0);
      return null;
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      e.target.value = ""; // Reset input
    }
  };

  const removeImage = async (imageId) => {
    const imageToRemove = images.find((img) => img.id === imageId);
    
    // Optimistically remove from UI
    onImagesChange(images.filter((img) => img.id !== imageId));
    
    // If image was uploaded to server, delete it there too
    if (imageToRemove?.key) {
      try {
        await axios.post(baseURL + "/quote-image-delete", {
          fileKey: imageToRemove.key,
          quoteRef: getQuoteRef(),
          quoteSessionId: quoteSessionId,
          prospectEmail,
          prospectMobile,
        });
      } catch (err) {
        console.error("Failed to delete image from server:", err);
        // Don't show error - image is already removed from UI
      }
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="quote-image-upload">
      <div className="quote-image-upload-header">
        <div className="quote-image-upload-title">
          <i className="mdi mdi-camera"></i>
          <span>Add Photos of Your Items</span>
          <span className="quote-image-upload-optional">(Optional)</span>
        </div>
        <p className="quote-image-upload-subtitle">
          Photos help us give you a more accurate quote and help drivers prepare for your move
        </p>
      </div>

      {/* Image Previews */}
      {images.length > 0 && (
        <div className="quote-image-previews">
          {images.map((image) => (
            <div key={image.id} className="quote-image-preview">
              <img src={image.thumbnail || image.url} alt={image.name} />
              <button
                className="quote-image-remove"
                onClick={() => removeImage(image.id)}
                type="button"
                aria-label="Remove image"
              >
                <i className="mdi mdi-close"></i>
              </button>
            </div>
          ))}
          
          {/* Add More Button (if under limit) */}
          {images.length < MAX_IMAGES && !uploading && (
            <button
              className="quote-image-add-more"
              onClick={openFilePicker}
              type="button"
            >
              <i className="mdi mdi-plus"></i>
              <span>Add</span>
            </button>
          )}
        </div>
      )}

      {/* Upload Area (show if no images or still have slots) */}
      {images.length === 0 && (
        <div
          className={`quote-image-dropzone ${dragActive ? "active" : ""} ${uploading ? "uploading" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={!uploading ? openFilePicker : undefined}
        >
          {uploading ? (
            <div className="quote-image-uploading">
              <div className="quote-image-spinner"></div>
              <span>Uploading... {uploadProgress}%</span>
              <div className="quote-image-progress">
                <div 
                  className="quote-image-progress-bar" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          ) : (
            <>
              <div className="quote-image-dropzone-icon">
                <i className="mdi mdi-cloud-upload"></i>
              </div>
              <div className="quote-image-dropzone-text">
                <span className="quote-image-dropzone-main">
                  Tap to add photos
                </span>
                <span className="quote-image-dropzone-sub">
                  or drag and drop here
                </span>
              </div>
              <div className="quote-image-dropzone-hint">
                JPG, PNG, WebP • Max {MAX_FILE_SIZE_MB}MB each • Up to {MAX_IMAGES} photos
              </div>
            </>
          )}
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        multiple
        onChange={handleInputChange}
        style={{ display: "none" }}
      />

      {/* Error Message */}
      {error && (
        <div className="quote-image-error">
          <i className="mdi mdi-alert-circle"></i>
          {error}
        </div>
      )}

      {/* Image Count */}
      {images.length > 0 && (
        <div className="quote-image-count">
          <i className="mdi mdi-image-multiple"></i>
          {images.length} of (up to) {MAX_IMAGES} photos added
        </div>
      )}

      {/* AI Tip */}
      <div className="quote-image-tip">
        <i className="mdi mdi-lightbulb-on-outline"></i>
        <span>
          <strong>Tip:</strong> Include photos of large items, fragile goods, 
          or anything that needs special handling
        </span>
      </div>
    </div>
  );
};

export default QuoteImageUpload;
